import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Stockage résilient : dans une iframe tierce (SSO Navixy), Firefox/Safari peuvent cloisonner ou BLOQUER localStorage.
// On garde donc une copie en mémoire (survit à la session de l'onglet) et on écrit localStorage en best-effort.
const _mem = {};
const safeGet = (k) => {
  try {
    const v = localStorage.getItem(k);
    if (v !== null) return v;
  } catch { /* storage bloqué (iframe tierce) */ }
  return _mem[k] ?? null;
};
const safeSet = (k, v) => {
  if (v == null) {
    delete _mem[k];
    try { localStorage.removeItem(k); } catch { /* ignore */ }
  } else {
    _mem[k] = v;
    try { localStorage.setItem(k, v); } catch { /* ignore */ }
  }
};

const TOKEN_KEY = "lt_token";
export const getToken = () => safeGet(TOKEN_KEY);
export const setToken = (t) => safeSet(TOKEN_KEY, t || null);

// Vue client (superadmin uniquement — le backend vérifie le rôle côté serveur)
const ACTING_KEY = "lt_acting_tenant";
export const getActingTenant = () => {
  try {
    return JSON.parse(safeGet(ACTING_KEY)) || null;
  } catch {
    return null;
  }
};
export const setActingTenant = (t) => safeSet(ACTING_KEY, t ? JSON.stringify(t) : null);

// Impersonation (superadmin → utilisateur précis d'un client) : le jeton de session devient celui de la cible (droits
// EXACTS relus côté serveur) ; le jeton superadmin est conservé localement pour être restauré à la sortie.
const SA_TOKEN_KEY = "lt_sa_token";
const IMPERSONATION_KEY = "lt_impersonation";
export const getImpersonation = () => {
  try {
    return JSON.parse(safeGet(IMPERSONATION_KEY)) || null;
  } catch {
    return null;
  }
};
export const beginImpersonation = (data) => {
  safeSet(SA_TOKEN_KEY, getToken() || "");
  safeSet(IMPERSONATION_KEY, JSON.stringify({
    session_id: data.session_id, email: data.user?.email, role: data.user?.role,
    tenant_id: data.tenant?.id, tenant_name: data.tenant?.name,
  }));
  setActingTenant(null);
  setToken(data.token);
};
// Restaure la session superadmin (sans appel réseau) — utilisé après l'appel `/auth/impersonation/end` ou sur 401.
export const restoreSuperadminSession = () => {
  const sa = safeGet(SA_TOKEN_KEY);
  safeSet(IMPERSONATION_KEY, null);
  safeSet(SA_TOKEN_KEY, null);
  setActingTenant(null);
  setToken(sa || null);
  return !!sa;
};

// Vrai quand l'app tourne dans une iframe (ouverture via le hub Navixy) → ne jamais rediriger vers /login.
export const isEmbedded = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

const http = axios.create({ baseURL: API });
http.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  const acting = getActingTenant();
  if (acting?.id) config.headers["X-Acting-Tenant"] = acting.id;
  return config;
});
http.interceptors.response.use(
  (r) => r,
  (error) => {
    const url = String(error?.config?.url || "");
    if (error?.response?.status === 401 && !url.includes("/auth/login")
        && !url.includes("/auth/change-password") && !url.includes("/auth/navixy/exchange")
        && !url.includes("/auth/me")) {
      if (getImpersonation()) {
        // Session d'assistance expirée/révoquée : retour à la console superadmin, jamais à l'écran de connexion
        const restored = restoreSuperadminSession();
        window.location.assign(restored ? "/admin?assist=ended" : "/login");
        return Promise.reject(error);
      }
      setToken(null);
      // Dans l'iframe Navixy, une page /login est inutile : on laisse l'app re-déclencher le SSO via le session_key.
      if (!isEmbedded() && !window.location.pathname.startsWith("/login")) window.location.assign("/login");
    }
    return Promise.reject(error);
  }
);

// Jeton fichier court (scope=file, 10 min) — le JWT de session n'est JAMAIS mis en URL
let fileToken = null;
export const refreshFileToken = () => {
  const acting = getActingTenant();
  return http
    .get("/auth/file-token", { params: acting?.id ? { acting_tenant: acting.id } : {} })
    .then((r) => {
      fileToken = r.data.token;
      return fileToken;
    });
};
export const clearFileToken = () => {
  fileToken = null;
};

const withToken = (url) => {
  if (!fileToken) return url;
  return `${url}${url.includes("?") ? "&" : "?"}token=${encodeURIComponent(fileToken)}`;
};

// Authentification
export const authLogin = (email, password) =>
  http.post("/auth/login", { email, password }).then((r) => r.data);
export const authMe = () => http.get("/auth/me").then((r) => r.data);
export const exchangeNavixy = (session_key) =>
  http.post("/auth/navixy/exchange", { session_key }).then((r) => r.data);
export const authLogout = () => http.post("/auth/logout").then((r) => r.data);
export const adminImpersonate = (tid, user_id) => http.post(`/admin/tenants/${tid}/impersonate`, { user_id }).then((r) => r.data);
export const endImpersonation = () => http.post("/auth/impersonation/end").then((r) => r.data);
export const authChangePassword = (current_password, new_password) =>
  http.post("/auth/change-password", { current_password, new_password }).then((r) => r.data);

export const fileUrl = (path, { download = false, filename } = {}) => {
  if (!path) return "";
  const params = new URLSearchParams();
  if (download) params.set("download", "true");
  if (filename) params.set("filename", filename);
  const qs = params.toString();
  return withToken(`${API}/files/${path}${qs ? `?${qs}` : ""}`);
};

// Resolve a media reference that may be an external url or a storage path
export const mediaSrc = (item) => {
  if (!item) return "";
  if (item.path) return fileUrl(item.path);
  return item.url || "";
};

// Ajoute le jeton aux URLs de photos stockées pointant vers /api/files
export const photoSrc = (url) => (url && url.includes("/api/files/") ? withToken(url) : url);

export const vehicleThumbSrc = (v) =>
  v?.photo?.thumb_path
    ? photoSrc(`/api/files/${v.photo.thumb_path}`)
    : v?.photo_url
      ? photoSrc(v.photo_url)
      : null;

export const setVehiclePhoto = (vehicleId, file, replace = false) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("replace", replace ? "true" : "false");
  return http.post(`/vehicles/${vehicleId}/photo`, fd).then((r) => r.data);
};

export const setVehiclePhotoFromDocument = (vehicleId, documentId, replace = false) =>
  http.post(`/vehicles/${vehicleId}/photo/from-document`, { document_id: documentId, replace }).then((r) => r.data);

export const deleteVehiclePhoto = (vehicleId) =>
  http.delete(`/vehicles/${vehicleId}/photo`).then((r) => r.data);

export const retryVehiclePhotoNavixy = (vehicleId) =>
  http.post(`/vehicles/${vehicleId}/photo/navixy/push`).then((r) => r.data);

export const importVehiclePhotoFromNavixy = (vehicleId, replace = false) =>
  http.post(`/vehicles/${vehicleId}/photo/navixy/import`, { replace }).then((r) => r.data);

// Vehicles
export const getVehicles = () => http.get("/vehicles").then((r) => r.data);
export const getVehicle = (id) => http.get(`/vehicles/${id}`).then((r) => r.data);
export const createVehicle = (data) => http.post("/vehicles", data).then((r) => r.data);
export const updateVehicle = (id, data) => http.put(`/vehicles/${id}`, data).then((r) => r.data);
export const deleteVehicle = (id) => http.delete(`/vehicles/${id}`).then((r) => r.data);

// Documents
export const getDocuments = (id) => http.get(`/vehicles/${id}/documents`).then((r) => r.data);
export const deleteDocument = (docId) => http.delete(`/documents/${docId}`).then((r) => r.data);
export const uploadDocument = (vehicleId, file, folder, onProgress) => {
  const form = new FormData();
  form.append("file", file);
  form.append("folder", folder);
  return http
    .post(`/vehicles/${vehicleId}/documents`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: onProgress,
    })
    .then((r) => r.data);
};

// Generic upload (photos / inspection media)
export const uploadFile = (file, vehicleId = "misc") => {
  const form = new FormData();
  form.append("file", file);
  form.append("vehicle_id", vehicleId);
  return http
    .post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } })
    .then((r) => r.data);
};

// Inspections
export const getInspections = (id) => http.get(`/vehicles/${id}/inspections`).then((r) => r.data);
export const createInspection = (id, data) =>
  http.post(`/vehicles/${id}/inspections`, data).then((r) => r.data);
export const deleteInspection = (insId) => http.delete(`/inspections/${insId}`).then((r) => r.data);

// Dashboard & timeline
export const getDashboard = () => http.get("/dashboard").then((r) => r.data);
export const getTimeline = () => http.get("/timeline").then((r) => r.data);

// Échéances — moteur central V2 (étape 4) + seuils par client
export const getDeadlines = (params = {}) => http.get("/deadlines", { params }).then((r) => r.data);
export const getDeadlineSettings = () => http.get("/settings/deadlines").then((r) => r.data);
export const putDeadlineSettings = (data) => http.put("/settings/deadlines", data).then((r) => r.data);

// Coûts — moteur central dérivé des documents V2 + legacy (dual-read)
export const getCosts = (params = {}) => http.get("/costs", { params }).then((r) => r.data);
export const getVehicleCosts = (id) => http.get(`/vehicles/${id}/costs`).then((r) => r.data);
export const getBusinessCategories = () => http.get("/business-categories").then((r) => r.data);
// Amende : marquer payée / retour « à payer » (action explicite auditée, rien n'est supprimé)
// Lot D : paid_on = date métier (saisie), payment_ref optionnelle — paid_at (technique) est posé par le serveur.
export const setDocumentPaid = (id, payee, extra = {}) => http.post(`/documents/${id}/paid`, { payee, ...extra }).then((r) => r.data);

// Phase 4C — Lot D : amendes (10 statuts, paiement métier, pièces liées, historique, KPI, exports)
export const getFines = (params = {}) => http.get("/fines", { params }).then((r) => r.data);
export const getFinesStats = () => http.get("/fines/stats").then((r) => r.data);
export const getFine = (id) => http.get(`/fines/${id}`).then((r) => r.data);
export const setFineStatus = (id, data) => http.post(`/documents/${id}/fine-status`, data).then((r) => r.data);
export const getDocumentHistory = (id) => http.get(`/documents/${id}/history`).then((r) => r.data);
export const getFineAttachments = (id) => http.get(`/documents/${id}/attachments`).then((r) => r.data);
export const addFineAttachment = (id, { piece_type, titre, date_piece, note, file }) => {
  const form = new FormData();
  form.append("piece_type", piece_type);
  form.append("titre", titre);
  if (date_piece) form.append("date_piece", date_piece);
  if (note) form.append("note", note);
  if (file) form.append("file", file);
  return http.post(`/documents/${id}/attachments`, form, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const removeFineAttachment = (id, attId) => http.delete(`/documents/${id}/attachments/${attId}`).then((r) => r.data);
export const finesExportUrl = (fmt, params = {}) => {
  const qs = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "").map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
  return withToken(`${API}/reports/amendes.${fmt}${qs ? `?${qs}` : ""}`);
};

// Énergie & carburant — fuel_transactions dérivées des tickets validés (jamais resommées dans Coûts)
export const getEnergy = (params = {}) => http.get("/energy", { params }).then((r) => r.data);
export const getVehicleEnergy = (id) => http.get(`/vehicles/${id}/energy`).then((r) => r.data);

// Synchronisation télématique
export const getNavixyStatus = () => http.get("/navixy/status").then((r) => r.data);
export const navixySync = () => http.post("/navixy/sync").then((r) => r.data);

// Alerts
export const getAlerts = () => http.get("/alerts").then((r) => r.data);
export const getAlertsLog = () => http.get("/alerts/log").then((r) => r.data);
export const runAlerts = () => http.post("/alerts/run").then((r) => r.data);

// Scan intelligent de documents
export const getDocumentTypes = () => http.get("/document-types").then((r) => r.data);
export const getDocumentExtraction = (docId) =>
  http.get(`/documents/${docId}/extraction`).then((r) => r.data);

export const suggestReservoir = (vehicleId) =>
  http.post(`/vehicles/${vehicleId}/reservoir/suggest`).then((r) => r.data);

export const applyReservoir = (vehicleId, valueL) =>
  http.post(`/vehicles/${vehicleId}/reservoir/apply`, { value_l: valueL }).then((r) => r.data);

export const suggestConso = (vehicleId) =>
  http.post(`/vehicles/${vehicleId}/conso/suggest`).then((r) => r.data);

export const applyConso = (vehicleId, payload) =>
  http.post(`/vehicles/${vehicleId}/conso/apply`, payload).then((r) => r.data);

export const suggestCo2 = (vehicleId) =>
  http.post(`/vehicles/${vehicleId}/co2/suggest`).then((r) => r.data);

export const applyCo2 = (vehicleId, payload) =>
  http.post(`/vehicles/${vehicleId}/co2/apply`, payload).then((r) => r.data);

export const pushVehicleNavixy = (vehicleId) =>
  http.post(`/vehicles/${vehicleId}/navixy/push`).then((r) => r.data);

export const scanVehicleDocument = (vehicleId, files, { documentType, documentId, asPdf } = {}) => {
  const form = new FormData();
  (files || []).forEach((f) => form.append("files", f));
  if (documentType) form.append("document_type", documentType);
  if (documentId) form.append("document_id", documentId);
  if (asPdf) form.append("as_pdf", "1");
  return http
    .post(`/vehicles/${vehicleId}/documents/scan`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 180000,
    })
    .then((r) => r.data);
};
export const validateScannedDocument = (docId, payload) =>
  http.post(`/documents/${docId}/validate`, payload).then((r) => r.data);
export const getPendingReviewCount = () =>
  http.get("/documents/pending-review-count").then((r) => r.data);
export const getFieldMeta = (id) => http.get(`/vehicles/${id}/field-meta`).then((r) => r.data);
export const getVehicleHistory = (id) => http.get(`/vehicles/${id}/history`).then((r) => r.data);

// Enrichissement technique — base officielle ASTRA/OFROU (locale, sans clé)
export const getTechnicalStatus = () => http.get("/technical-data/status").then((r) => r.data);
export const getConfigStatus = () => http.get("/config/status").then((r) => r.data);
export const getAstraStatus = () => http.get("/astra/status").then((r) => r.data);
export const astraImport = () => http.post("/astra/import").then((r) => r.data);
export const enrichFleet = () =>
  http.post("/vehicles/enrich-technical/batch", null, { timeout: 120000 }).then((r) => r.data);
export const revertTechnicalField = (id, field) =>
  http.post(`/vehicles/${id}/enrich-technical/revert`, { field }).then((r) => r.data);
export const getConsumptionRanking = () =>
  http.get("/fleet/consumption-ranking").then((r) => r.data);
export const getIntegrity = (params = {}) =>
  http.get("/fleet/integrity", { params }).then((r) => r.data);
export const getVehicleTelematics = (id) => http.get(`/vehicles/${id}/telematics`).then((r) => r.data);
export const getLinkSuggestions = () =>
  http.get("/integrations/navixy/link-suggestions").then((r) => r.data);
export const linkNavixyVehicle = (vehicleId, externalVehicleId) =>
  http.post("/integrations/navixy/link", { vehicle_id: vehicleId, external_vehicle_id: externalVehicleId }).then((r) => r.data);
export const createNavixyVehicle = (vehicleId, confirm = false) =>
  http.post("/integrations/navixy/create-vehicle", { vehicle_id: vehicleId, confirm }).then((r) => r.data);
export const conformityReportUrl = () => withToken(`${API}/reports/conformite.pdf`);
export const costsCsvUrl = () => withToken(`${API}/reports/couts.csv`);
export const vehicleReportUrl = (id) => withToken(`${API}/reports/vehicule/${id}.pdf`);
export const enrichTechnical = (id) =>
  http.post(`/vehicles/${id}/enrich-technical`, null, { timeout: 60000 }).then((r) => r.data);
export const applyTechnicalEnrichment = (id, payload) =>
  http.post(`/vehicles/${id}/enrich-technical/apply`, payload).then((r) => r.data);

// Console Super Admin
export const adminOverview = () => http.get("/admin/overview").then((r) => r.data);
export const adminCreateTenant = (data) => http.post("/admin/tenants", data).then((r) => r.data);
export const adminUpdateTenant = (tid, data) => http.put(`/admin/tenants/${tid}`, data).then((r) => r.data);
export const adminListUsers = (tid) => http.get(`/admin/tenants/${tid}/users`).then((r) => r.data);
export const adminCreateUser = (tid, data) => http.post(`/admin/tenants/${tid}/users`, data).then((r) => r.data);
export const adminUpdateUser = (uid, data) => http.put(`/admin/users/${uid}`, data).then((r) => r.data);
export const adminGetIntegration = (tid) => http.get(`/admin/tenants/${tid}/integration`).then((r) => r.data);
export const adminUpdateIntegration = (tid, data) =>
  http.put(`/admin/tenants/${tid}/integration`, data).then((r) => r.data);

// Documents V2 — page centrale, fiche, catégories, profils
export const getAllDocuments = (params = {}) => http.get("/documents", { params }).then((r) => r.data);
export const aiStatus = () => http.get("/ai/status").then((r) => r.data);
export const aiAssistant = (question, temperature = 0.2) => http.post("/ai/assistant", { question, temperature }).then((r) => r.data);
export const aiVehicleCompliance = (vehicleId) => http.post(`/ai/vehicles/${vehicleId}/compliance`).then((r) => r.data);
export const aiFineLetter = (fineId, body = {}) => http.post(`/ai/fines/${fineId}/letter`, body).then((r) => r.data);
export const aiDocumentSummary = (docId) => http.post(`/ai/documents/${docId}/summary`).then((r) => r.data);
export const getVehiclesArchive = () => http.get("/vehicles-archive").then((r) => r.data);
export const getArchiveDocuments = (id) => http.get(`/vehicles-archive/${id}/documents`).then((r) => r.data);
export const restoreArchivedVehicle = (id) => http.post(`/vehicles-archive/${id}/restore`).then((r) => r.data);
export const adminListTenantVehicles = (tid) => http.get(`/admin/tenants/${tid}/vehicles`).then((r) => r.data);
export const adminTransferArchive = (data) => http.post("/admin/vehicles-archive/transfer", data).then((r) => r.data);
export const adminListTransfers = () => http.get("/admin/transfers").then((r) => r.data);
export const updateDocument = (docId, data) => http.patch(`/documents/${docId}`, data).then((r) => r.data);
export const getDocCategories = () => http.get("/doc-categories").then((r) => r.data);
export const createDocCategory = (data) => http.post("/doc-categories", data).then((r) => r.data);
export const updateDocCategory = (id, data) => http.put(`/doc-categories/${id}`, data).then((r) => r.data);
export const deleteDocCategory = (id) => http.delete(`/doc-categories/${id}`).then((r) => r.data);
export const getDocRequirements = () => http.get("/doc-requirements").then((r) => r.data);
export const putDocRequirements = (profil, categories) =>
  http.put("/doc-requirements", { profil, categories }).then((r) => r.data);
export const getVehicleDocConformity = (id) =>
  http.get(`/vehicles/${id}/conformite-documents`).then((r) => r.data);

// Phase 4C — Lot A : correspondances legacy Journal → Documents (lecture seule + confirmation humaine)
export const getLegacyVehicleMap = (params = {}) => http.get("/legacy/vehicle-map", { params }).then((r) => r.data);
export const legacyVehicleCandidates = (data) => http.post("/legacy/vehicle-map/candidates", data).then((r) => r.data);
export const legacyVehicleStage = (data) => http.post("/legacy/vehicle-map/stage", data).then((r) => r.data);
export const legacyVehicleConfirm = (legacyId, data, legacySource = "journal") =>
  http.post(`/legacy/vehicle-map/${encodeURIComponent(legacyId)}/confirm`, data, { params: { legacy_source: legacySource } }).then((r) => r.data);
export const legacyVehicleReject = (legacyId, reason, legacySource = "journal") =>
  http.post(`/legacy/vehicle-map/${encodeURIComponent(legacyId)}/reject`, { reason }, { params: { legacy_source: legacySource } }).then((r) => r.data);
export const adminLegacyTenantMap = () => http.get("/admin/legacy/tenant-map").then((r) => r.data);
export const adminLegacyTenantCandidates = (data) => http.post("/admin/legacy/tenant-map/candidates", data).then((r) => r.data);
export const adminLegacyTenantConfirm = (data) => http.post("/admin/legacy/tenant-map/confirm", data).then((r) => r.data);
export const adminLegacyTenantRevoke = (data) => http.post("/admin/legacy/tenant-map/revoke", data).then((r) => r.data);

// Phase 4C — Lot B : documents métier SANS fichier (D1) + justificatif joint a posteriori
export const createFuelNoFile = (vehicleId, data) => http.post(`/vehicles/${vehicleId}/fuel-transactions`, data).then((r) => r.data);
export const createFineNoFile = (vehicleId, data) => http.post(`/vehicles/${vehicleId}/fines`, data).then((r) => r.data);
export const attachDocumentFile = (docId, file) => {
  const form = new FormData();
  form.append("file", file);
  return http.post(`/documents/${docId}/attach-file`, form, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

// Phase 4C — Lot C : conducteurs (référentiel tenant) + affectations datées véhicule↔conducteur
export const getDrivers = (params = {}) => http.get("/drivers", { params }).then((r) => r.data);
export const getDriver = (id) => http.get(`/drivers/${id}`).then((r) => r.data);
export const createDriver = (data) => http.post("/drivers", data).then((r) => r.data);
export const updateDriver = (id, data) => http.patch(`/drivers/${id}`, data).then((r) => r.data);
export const archiveDriver = (id) => http.post(`/drivers/${id}/archive`).then((r) => r.data);
export const restoreDriver = (id) => http.post(`/drivers/${id}/restore`).then((r) => r.data);
export const getVehicleAssignments = (vehicleId) => http.get(`/vehicles/${vehicleId}/driver-assignments`).then((r) => r.data);
export const createVehicleAssignment = (vehicleId, data) => http.post(`/vehicles/${vehicleId}/driver-assignments`, data).then((r) => r.data);
export const closeAssignment = (id, data) => http.post(`/driver-assignments/${id}/close`, data).then((r) => r.data);
export const getDriverAt = (vehicleId, date) => http.get(`/vehicles/${vehicleId}/driver-at`, { params: date ? { date } : {} }).then((r) => r.data);

// Phase 4C — Lot E : cartes carburant (référentiel + affectations datées + resolve lecture seule)
export const getFuelCards = (params = {}) => http.get("/fuel-cards", { params }).then((r) => r.data);
export const getFuelCard = (id) => http.get(`/fuel-cards/${id}`).then((r) => r.data);
export const createFuelCard = (data) => http.post("/fuel-cards", data).then((r) => r.data);
export const updateFuelCard = (id, data) => http.patch(`/fuel-cards/${id}`, data).then((r) => r.data);
export const setFuelCardStatus = (id, data) => http.post(`/fuel-cards/${id}/status`, data).then((r) => r.data);
export const archiveFuelCard = (id, data) => http.post(`/fuel-cards/${id}/archive`, data).then((r) => r.data);
export const restoreFuelCard = (id, data = {}) => http.post(`/fuel-cards/${id}/restore`, data).then((r) => r.data);
export const getFuelCardAssignments = (id) => http.get(`/fuel-cards/${id}/assignments`).then((r) => r.data);
export const createFuelCardAssignment = (id, data) => http.post(`/fuel-cards/${id}/assignments`, data).then((r) => r.data);
export const closeFuelCardAssignment = (assignmentId, data = {}) => http.post(`/fuel-card-assignments/${assignmentId}/close`, data).then((r) => r.data);
export const getFuelCardHistory = (id) => http.get(`/fuel-cards/${id}/history`).then((r) => r.data);
export const resolveFuelCard = (params) => http.get("/fuel-cards/resolve", { params }).then((r) => r.data);

// Phase 4C — Lot F : imports carburant, rattachement, anomalies
export const getFuelImportFields = () => http.get("/fuel/import-fields").then((r) => r.data);
export const uploadFuelImport = (file, fournisseur) => {
  const fd = new FormData();
  fd.append("file", file);
  if (fournisseur) fd.append("fournisseur", fournisseur);
  return http.post("/fuel/imports", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const getFuelImports = () => http.get("/fuel/imports").then((r) => r.data);
export const getFuelImport = (id) => http.get(`/fuel/imports/${id}`).then((r) => r.data);
export const getFuelImportRows = (id, params = {}) => http.get(`/fuel/imports/${id}/rows`, { params }).then((r) => r.data);
export const setFuelImportMapping = (id, data) => http.post(`/fuel/imports/${id}/mapping`, data).then((r) => r.data);
export const resolveFuelImportRow = (jobId, rowId, data) => http.patch(`/fuel/imports/${jobId}/rows/${rowId}`, data).then((r) => r.data);
export const acceptUniqueFuelImportRows = (jobId, data) => http.post(`/fuel/imports/${jobId}/rows/accept-unique`, data).then((r) => r.data);
export const confirmFuelImport = (id) => http.post(`/fuel/imports/${id}/confirm`).then((r) => r.data);
export const forceFuelImportRow = (jobId, rowId, data) => http.post(`/fuel/imports/${jobId}/rows/${rowId}/force`, data).then((r) => r.data);
export const getFuelTransaction = (id) => http.get(`/fuel-transactions/${id}`).then((r) => r.data);
export const matchFuelTransaction = (id, data) => http.patch(`/fuel-transactions/${id}/match`, data).then((r) => r.data);
export const setFuelTransactionCard = (id, data) => http.patch(`/fuel-transactions/${id}/card`, data).then((r) => r.data);
export const runFuelMatch = () => http.post("/fuel/match/run").then((r) => r.data);
export const getFuelAnomalies = (params = {}) => http.get("/fuel/anomalies", { params }).then((r) => r.data);
export const scanFuelAnomalies = () => http.post("/fuel/anomalies/scan").then((r) => r.data);
export const decideFuelAnomaly = (id, data) => http.post(`/fuel/anomalies/${id}/decide`, data).then((r) => r.data);
export const getFuelSettings = () => http.get("/tenant-settings/fuel").then((r) => r.data);

// Phase 4C — Lot G : rapprochements achats ↔ consommation, décomptes (statements), verrou, exports audités
export const getFuelReconciliations = (params) => http.get("/fuel/reconciliations", { params }).then((r) => r.data);
export const justifyFuelReconciliation = (vehicleId, period, data) => http.post(`/fuel/reconciliations/${vehicleId}/${period}/justify`, data).then((r) => r.data);
export const getReconciliationSettings = () => http.get("/tenant-settings/fuel/reconciliation").then((r) => r.data);
export const updateReconciliationSettings = (data) => http.patch("/tenant-settings/fuel/reconciliation", data).then((r) => r.data);
export const getFuelStatements = (params = {}) => http.get("/fuel/statements", { params }).then((r) => r.data);
export const getFuelStatement = (id) => http.get(`/fuel/statements/${id}`).then((r) => r.data);
export const createFuelStatement = (data) => http.post("/fuel/statements", data).then((r) => r.data);
export const recalculateFuelStatement = (id) => http.post(`/fuel/statements/${id}/recalculate`).then((r) => r.data);
export const setFuelStatementDeclared = (id, data) => http.patch(`/fuel/statements/${id}/declared`, data).then((r) => r.data);
export const closeFuelStatement = (id) => http.post(`/fuel/statements/${id}/close`).then((r) => r.data);
export const closeFuelStatementException = (id, data) => http.post(`/fuel/statements/${id}/close-exception`, data).then((r) => r.data);
export const downloadFuelExport = async (path, params) => {
  const r = await http.get(path, { params, responseType: "blob" });
  const m = /filename="?([^";]+)"?/.exec(r.headers["content-disposition"] || "");
  const name = m ? m[1] : `export.${params.format}`;
  const url = URL.createObjectURL(r.data);
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { filename: name, size: r.data.size, sha256: r.headers["x-content-sha256"] };
};

// Phase 4C — Lot H : vues chauffeur « mes données » (self-scope serveur) + console superadmin (liaison conducteur / scope manager)
export const getMyProfile = () => http.get("/me/profile").then((r) => r.data);
export const getMyVehicles = () => http.get("/me/vehicles").then((r) => r.data);
export const getMyFuelTransactions = (params = {}) => http.get("/me/fuel-transactions", { params }).then((r) => r.data);
export const getMyFines = () => http.get("/me/fines").then((r) => r.data);
export const attachMyFuelReceipt = (txId, file) => {
  const form = new FormData();
  form.append("file", file);
  return http.post(`/me/fuel-transactions/${txId}/attachment`, form, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const adminListTenantDrivers = (tid) => http.get(`/admin/tenants/${tid}/drivers`).then((r) => r.data);

export default http;
